import { Component, OnInit, inject } from '@angular/core';
import { debounce } from 'lodash-es';
import { UserSettings } from 'app/core/enums/user-settings.enum';
import { UserSettingsService } from 'app/core/services/user-settings.service';
import { LimitOffsetPaging } from 'app/core/models/paged-results/limit-offset-paging.model';
import { ClientsService } from 'app/core/services/clients.service';
import { UserAssignedClientsService } from 'app/core/services/user-assigned-clients.service';

import { FormsModule } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';

import { RouterLinkActive, RouterLink } from '@angular/router';
import { PaginationHeaderComponent } from '../../../shared/components/pagination-header/pagination-header.component';
import { PaginationComponent } from '../../../shared/components/pagination/pagination.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ClientSelectComponent } from '../../../shared/components/client-select/client-select.component';
import {
  EncounterTypeEligibilityFilters,
  EncounterTypeEligibilityListSettings,
  EncounterTypeEligibilityUiOptions
} from 'app/core/models/user-settings/encounter-type-eligibility-settings.model';
import { EncounterTypeEligibilitySummary } from 'app/core/models/encounter-type-eligibility-summary.model';
import { EncounterTypeEligibilityStatusOptions } from 'app/core/options/encounter-type-eligibility-status.opts';
import { EncounterTypeEligibilitiesService } from 'app/core/services/encounter-type-eligibilities.service';
import { EncounterTypeEligibilitySummaryListComponent } from 'app/shared/components/encounter-type-eligibility-summary-list/encounter-type-eligibility-summary-list.component';
import { EncounterTypeEligibilitiesExplainerComponent } from 'app/shared/components/encounter-type-eligibilities-explainer/encounter-type-eligibilities-explainer.component';
import { EncountersService } from 'app/core/services/encounters.service';

@Component({
    selector: 'app-capture-admin-encounter-type-eligibilities',
    templateUrl: './capture-admin-encounter-type-eligibilities.component.html',
    styleUrls: ['./capture-admin-encounter-type-eligibilities.component.scss'],
    imports: [EncounterTypeEligibilitySummaryListComponent,
              EncounterTypeEligibilitiesExplainerComponent,
              FormsModule,
              NgSelectComponent,
              ClientSelectComponent,
              RouterLinkActive,
              RouterLink,
              PaginationHeaderComponent,
              PaginationComponent,
              MatProgressSpinner]
})
export class CaptureAdminEncounterTypeEligibilitiesComponent implements OnInit {
  private encounterTypeEligibilitiesService = inject(EncounterTypeEligibilitiesService);
  private clientsService = inject(ClientsService);
  private userSettingsService = inject(UserSettingsService);
  private userAssignedClientsService = inject(UserAssignedClientsService);
  private encountersService = inject(EncountersService);

  encounterTypeEligibilitySummaries: EncounterTypeEligibilitySummary[] = [];
  paging: LimitOffsetPaging = LimitOffsetPaging.empty;
  filters: EncounterTypeEligibilityFilters = new EncounterTypeEligibilityFilters();
  uiOptions: EncounterTypeEligibilityUiOptions = new EncounterTypeEligibilityUiOptions();
  loading = true;

  // exportUrl = '/capture-admin/reports/patient-interaction-location-reports';
  exportUrl = null;

  clientEntityTypeFilterOptions = [];
  assignedClientLeadUsersFilterOptions = [];
  encounterTypeFilterOptions = [];
  eligibilityStatusFilterOptions = EncounterTypeEligibilityStatusOptions;

  ngOnInit() {
    this.loadClientEntityTypes();
    this.loadEncounterTypes();
    this.loadAssignedUsers();
    this.loadSettings();
    this.loadEncounterTypeEligibilitySummaries();
  }

  onFilterChange() {
    this.paging.currentPage = 1;
    this.saveSettings();
    this.loadEncounterTypeEligibilitySummaries();
  }

  onPageSizeChange(size: number) {
    this.paging.pageSize = size;
    this.loadEncounterTypeEligibilitySummaries();
    this.saveSettings();
  }

  onPageChange(_page: number) {
    this.loadEncounterTypeEligibilitySummaries();
    this.saveSettings();
  }

  onLocationKeyUp() {
    this.debounceLocationKeyUp();
  }

  onResetFiltersClick() {
    this.paging.currentPage = 1;
    this.filters = new EncounterTypeEligibilityFilters();
    this.loadEncounterTypeEligibilitySummaries();
    this.saveSettings();
  }

  onExplainerExpandOrCollapse(expanded: boolean) {
    this.uiOptions.explainerExpanded = expanded;
    this.saveSettings();
  }

  private loadEncounterTypeEligibilitySummaries() {
    this.loading = true;

    this.encounterTypeEligibilitiesService.getSummaryList(this.paging, this.filters).subscribe(result => {
      this.encounterTypeEligibilitySummaries = result.encounterTypeEligibilitySummaries;
      this.paging = result.meta.paging;
      this.loading = false;
    });
  }

  private loadSettings() {
    const listSettings: EncounterTypeEligibilityListSettings =
      this.userSettingsService.get<EncounterTypeEligibilityListSettings>(
        UserSettings.captureAdminEncounterTypeEligibilityListSettings
      );

    if (listSettings) {
      this.filters = listSettings.filters;
      this.paging = listSettings.paging;

      if (listSettings.uiOptions) {
        this.uiOptions = listSettings.uiOptions;
      }
    }
  }

  private saveSettings() {
    const listSettings = new EncounterTypeEligibilityListSettings();
    listSettings.filters = this.filters;
    listSettings.paging = this.paging;
    listSettings.uiOptions = this.uiOptions;
    this.userSettingsService.save<EncounterTypeEligibilityListSettings>(
      UserSettings.captureAdminEncounterTypeEligibilityListSettings,
      listSettings
    );
  }

  private loadAssignedUsers() {
    this.userAssignedClientsService.getList().subscribe(result => {
      this.assignedClientLeadUsersFilterOptions = result.users;
    });
  }

  private loadClientEntityTypes() {
    this.clientsService.getEntityTypes().subscribe(result => {
      this.clientEntityTypeFilterOptions = result;
    });
  }

  private loadEncounterTypes() {
    this.encountersService.getEncounterTypes().subscribe(result => this.encounterTypeFilterOptions = result);
  }

  private debounceLocationKeyUp = debounce(
    () => { this.onFilterChange(); },
    1000,
    { maxWait: 2000 }
  );
}
